'use client';

import { dataLayerConfig } from '@aspendental/shared-components-web';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from './HeroLocation.module.scss';
import { AccountIdErrorMessage } from '../../AccountIdErrorMessage';
import Button from '../../Buttons/Button';
import EmergencyAlertDialog from '../../EmergencyAlertDialog';
import InjuryNotificationDialog from '../../InjuryNotificationDialog';
import LocationErrorMessage from '../../LocationErrorMessage';
import AcknowledgementModal from '../../Modals/AcknowledgementModal';
import { useFacilityContext } from '../../shared/providers/FacilityContextProvider';
import { OfficeDetails } from '@src/components/LocationCard/OfficeDetails';
import Tag from '@src/components/Tag/Tag';
import { shouldShowInjuryNotification } from '@src/constants/clinicTypes';
import { useXrayMaintenanceAcknowledgement, XRAY_MAINTENANCE_ACK_PRIMARY_BUTTON_TEXT, XRAY_MAINTENANCE_ACK_SECONDARY_BUTTON_TEXT, XRAY_MAINTENANCE_ACK_TITLE } from '@src/hooks/useXrayMaintenanceAcknowledgement';
import { useAppStore } from '@src/store';
import { useAppointmentTypesStore } from '@src/store/modules/appointment-types.store';
import { useAuthStore } from '@src/store/modules/auth.store';
import { useGTMStore } from '@src/store/modules/gtm.store';
import { useLocationStore } from '@src/store/modules/location.store';
import { getTypesByFacility, hasApptType } from '@src/utils/appointmentTypesUtils';
import { validateTags } from '@src/utils/tagValidation';
import { getApptTypeParam, getVisitReasonValidation, WORKPLACE_SCREENING_TAG } from '@src/utils/visitReasonValidation';
const getTagState = (disabled: boolean, isSelected: boolean): 'disabled' | 'selected' | 'default' => {
  if (disabled) return 'disabled';
  if (isSelected) return 'selected';
  return 'default';
};
export default function HeroLocation() {
  const {
    urgentCareCentersLink,
    training,
    setFlowType
  } = useAppStore();
  const {
    facility: facilityDetails,
    loading: facilityLoading,
    error: facilityError,
    getOfficeName,
    getOfficeAddress,
    getOfficeDistance
  } = useFacilityContext();
  const router = useRouter();
  const {
    location,
    setLocation,
    status,
    setStatus,
    selectedTag,
    setSelectedTag,
    selectedPhysicalTag,
    setSelectedPhysicalTag,
    setApptType,
    selectedDate,
    setSelectedDate,
    accountIdError,
    setAccountIdError
  } = useLocationStore();
  const {
    brand,
    setBrand,
    setWebsiteFacility,
    websiteFacilityId,
    websiteFacilityName
  } = useGTMStore();
  const {
    appointmentTypes,
    setAppointmentTypes,
    validTypes,
    setValidTypes
  } = useAppointmentTypesStore();
  const {
    ssId,
    setAccountId
  } = useAuthStore();
  const eventSentRef = useRef(false);
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [injuryDialogOpen, setInjuryDialogOpen] = useState(false);
  const {
    modalOpen: acknowledgementModalOpen,
    pendingSelection: pendingInjurySelection,
    requestAcknowledgement,
    closeModal: handleAcknowledgementModalClose,
    activeContent: acknowledgementModalContent
  } = useXrayMaintenanceAcknowledgement(facilityDetails?.siteId, facilityDetails?.ianaTimezone);
  const [showError, setShowError] = useState(false);
  const [showLocationError, setShowLocationError] = useState(false);
  const reasonForVisitRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setSelectedDate(new Date().toLocaleDateString('en-CA'));
    setStatus('default');
    setSelectedTag('');
    setSelectedPhysicalTag('');

    // Validate fc query parameter and facility details
    const fc = router.query.fc as string;
    const isValidFc = fc && fc.trim().length > 0;
    const hasFacilityError = facilityError !== null;
    setShowLocationError(!isValidFc || hasFacilityError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.fc, facilityError]);
  const validateTagsCallback = useCallback((availableKeys: string[]) => {
    return validateTags(availableKeys, facilityDetails?.clinicType);
  }, [facilityDetails?.clinicType]);
  const handleGetTypesByFacility = useCallback(async (latitude: string, longitude: string, siteId?: string) => {
    await getTypesByFacility(latitude, longitude, siteId, {
      facilityDetails,
      training,
      routerQuery: router.query,
      setStatus,
      setAccountId,
      setAppointmentTypes,
      setValidTypes,
      setLocation,
      setWebsiteFacility,
      setBrand,
      validateTags: validateTagsCallback,
      onAccountIdError: () => setAccountIdError(true)
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [facilityDetails, training, router.query, setStatus, setAccountId, setAppointmentTypes, setValidTypes, setLocation, setWebsiteFacility, setBrand, validateTagsCallback]);
  useEffect(() => {
    // Load appointment types when we have facility details
    if (facilityDetails && router.query.fc) {
      const initializeSession = async () => {
        // Use facility details coordinates
        const latitude = facilityDetails.latitude?.toString() || '';
        const longitude = facilityDetails.longitude?.toString() || '';
        const siteId = router.query.fc as string;

        // Load appointment types using utility function
        handleGetTypesByFacility(latitude, longitude, siteId);
      };
      initializeSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityDetails, router.query.fc]);
  useEffect(() => {
    // Add the clinic QS as the flowType
    if (router.query.clinic) {
      setFlowType('SS_KIOSK');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.clinic]);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Helper function to check if the clinic requires injury notification
  const shouldShowInjuryDialog = useCallback(() => {
    return shouldShowInjuryNotification(facilityDetails?.clinicType);
  }, [facilityDetails]);
  const handleTagSelect = useCallback((tag: string, key: string) => {
    const normalizedTag = tag.toLowerCase();
    const normalizedSelectedTag = selectedTag.toLowerCase();
    const isWorkplaceTag = tag === WORKPLACE_SCREENING_TAG;
    const isInjuryTag = key.toLowerCase() === 'injury';
    const isSelected = normalizedSelectedTag !== normalizedTag;

    // Injury selected at the 5239 location requires acknowledgement before proceeding
    if (isInjuryTag && isSelected && requestAcknowledgement({
      tag,
      key
    })) {
      return; // Don't proceed with normal tag selection
    }

    // Check if injury is selected at a clinic without x-ray services
    // Digging into this similar dialog, it checks the university clinic type to determine if the injury dialog should be shown.
    if (isInjuryTag && isSelected && shouldShowInjuryDialog()) {
      setInjuryDialogOpen(true);
      return; // Don't proceed with normal tag selection
    }
    setSelectedTag(isSelected ? tag : '');
    setApptType(isSelected ? key : '');
    if (isWorkplaceTag && isSelected && isMobile) {
      setTimeout(() => {
        if (reasonForVisitRef.current) {
          reasonForVisitRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, [selectedTag, setSelectedTag, setApptType, isMobile, shouldShowInjuryDialog, setInjuryDialogOpen, requestAcknowledgement]);
  const handleAcknowledgementPrimaryClick = useCallback(() => {
    if (pendingInjurySelection) {
      setSelectedTag(pendingInjurySelection.tag);
      setApptType(pendingInjurySelection.key);
    }
    router.push('/results');
  }, [pendingInjurySelection, setSelectedTag, setApptType, router]);
  const handleAcknowledgementSecondaryClick = useCallback(() => {
    window.location.href = urgentCareCentersLink || 'https://www.wellnow.com/urgent-care-centers/';
  }, [urgentCareCentersLink]);
  const handlePhysicalTagSelect = useCallback((tag: string) => {
    setSelectedPhysicalTag(selectedPhysicalTag === tag ? '' : tag);
    setApptType(tag);
  }, [selectedPhysicalTag, setSelectedPhysicalTag, setApptType]);
  const calculateHeight = useMemo(() => {
    switch (status) {
      case 'invalid':
        return '210px';
      case 'default':
        return '186px';
      case 'valid':
        return 'auto';
      default:
        return '186px';
    }
  }, [status]);
  useEffect(() => {
    if (eventSentRef.current || !brand || !ssId || !websiteFacilityId || !websiteFacilityName) return;
    dataLayerConfig({
      event: 'appointment_step',
      step_name: 'find_appointment',
      website_selected_facility_id: websiteFacilityId || '',
      website_selected_facility_name: websiteFacilityName || '',
      brand: brand || '',
      platform: 'nextjs',
      tag_session_id: ssId
    });
    eventSentRef.current = true;
  }, [ssId, websiteFacilityId, websiteFacilityName, brand]);
  const visitReasonValidation = getVisitReasonValidation(selectedTag, selectedPhysicalTag);
  const hasOmComplex = hasApptType(appointmentTypes, 'OM-COMPLEX');
  const hasOmStandard = hasApptType(appointmentTypes, 'OM-STANDARD');
  const omComplexState = getTagState(!hasOmComplex, selectedPhysicalTag === 'OM-COMPLEX');
  const omStandardState = getTagState(!hasOmStandard, selectedPhysicalTag === 'OM-STANDARD');
  const handleEmergencyTagClick = useCallback(() => {
    setSelectedTag('Experiencing chest pain, shortness of breath or stroke symptoms?');
    setApptType('');
    setEmergencyDialogOpen(true);
  }, [setSelectedTag, setApptType]);
  const handleScheduleClick = useCallback(() => {
    if (selectedTag === 'Experiencing chest pain, shortness of breath or stroke symptoms?') {
      setEmergencyDialogOpen(true);
      return;
    }
    if (!(visitReasonValidation.isValid && location)) {
      setShowError(true);
      if (reasonForVisitRef.current) {
        reasonForVisitRef.current.scrollIntoView({
          behavior: 'smooth'
        });
      }
      return;
    }
    setShowError(false);
    const routerFc = router.query.fc;
    let fcFromRouter = '';
    if (typeof routerFc === 'string') {
      fcFromRouter = routerFc;
    } else if (Array.isArray(routerFc)) {
      fcFromRouter = routerFc[0] ?? '';
    }
    const fcFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('fc') || '' : '';
    const fcFromFacility = facilityDetails?.siteId ? `WN_${facilityDetails.siteId}` : '';
    const fcParam = fcFromRouter || fcFromUrl || fcFromFacility;
    const normalizedDate = selectedDate && selectedDate !== '' ? selectedDate : new Date().toISOString().split('T')[0];
    router.push({
      pathname: '/results',
      query: {
        ...(fcParam ? {
          fc: fcParam
        } : {}),
        'appt-type': getApptTypeParam(selectedTag, selectedPhysicalTag),
        date: normalizedDate
      }
    });
  }, [facilityDetails?.siteId, location, router, selectedDate, selectedTag, selectedPhysicalTag, visitReasonValidation.isValid, setShowError]);
  return <Box className={classes['hero-location']} style={{
    height: `${calculateHeight}`
  }} data-sentry-element="Box" data-sentry-component="HeroLocation" data-sentry-source-file="HeroLocation.tsx">
			{facilityDetails && !facilityLoading ? <>
					<OfficeDetails name={getOfficeName()} office={facilityDetails?.siteName || ''} distance={getOfficeDistance()} address={getOfficeAddress()} border={true} edit={true} handleEditClick={() => {
        window.open(urgentCareCentersLink, '_blank');
      }} />
					{accountIdError && <AccountIdErrorMessage />}
				</> : !showLocationError && <Skeleton variant="rectangular" height={132} />}

			{!accountIdError && !showLocationError && (appointmentTypes && facilityDetails ? <Box className={classes['reason-for-visit']} ref={reasonForVisitRef}>
						<AcknowledgementModal open={acknowledgementModalOpen} onClose={handleAcknowledgementModalClose} title={XRAY_MAINTENANCE_ACK_TITLE} content={acknowledgementModalContent} primaryButtonText={XRAY_MAINTENANCE_ACK_PRIMARY_BUTTON_TEXT} secondaryButtonText={XRAY_MAINTENANCE_ACK_SECONDARY_BUTTON_TEXT} primaryButtonCallback={handleAcknowledgementPrimaryClick} secondaryButtonCallback={handleAcknowledgementSecondaryClick} />
						<Typography className={classes['reason-for-visit-text']}>Reason for visit</Typography>

						<Stack className={classes['tags-container']}>
							{validTypes.map(type => {
          const tagState = getTagState(Boolean(type.disabled), selectedTag === type.label);
          return <Tag key={type.label} label={type.label} state={tagState} onClick={() => handleTagSelect(type.label, type.key)} ariaLabel={`Select reason for visit: ${type.label}`} />;
        })}
							{/* Emergency Tag */}
							<Tag label="Experiencing chest pain, shortness of breath or stroke symptoms?" state={selectedTag === 'Experiencing chest pain, shortness of breath or stroke symptoms?' ? 'selected' : 'default'} onClick={handleEmergencyTagClick} ariaLabel="Emergency alert for chest pain, shortness of breath or stroke symptoms" />
						</Stack>
					</Box> : !showLocationError && <Box className={classes['reason-for-visit']}>
							<Typography className={classes['reason-for-visit-text']}>Reason for visit</Typography>
							<Stack className={classes['tags-container']}>
								<Skeleton variant="rectangular" height={38} width={64.63} style={{
          borderRadius: '8px'
        }} />
								<Skeleton variant="rectangular" height={38} width={61} style={{
          borderRadius: '8px'
        }} />
								<Skeleton variant="rectangular" height={38} width={183.44} style={{
          borderRadius: '8px'
        }} />
								<Skeleton variant="rectangular" height={38} width={213.66} style={{
          borderRadius: '8px'
        }} />
								<Skeleton variant="rectangular" height={38} width={63.58} style={{
          borderRadius: '8px'
        }} />
							</Stack>
						</Box>)}
			{selectedTag === WORKPLACE_SCREENING_TAG && <Box className={classes['physicals-question']}>
					<Typography className={classes['physicals-text']}>
						Will you require one or more of these types of physicals for your visit?
					</Typography>
					<Typography className={classes['physicals-list']}>
						Asbestos, civil service, coast guard, firefighter, ROTC, HAZMAT, or silica.
					</Typography>
					<Stack direction="row" spacing={1} className={classes['physicals-buttons']}>
						<Tag label={hasOmComplex ? 'Yes' : 'Yes (Not Available)'} state={omComplexState} onClick={() => {
          if (hasOmComplex) {
            handlePhysicalTagSelect('OM-COMPLEX');
          }
        }} ariaLabel="Yes, I require one of these types of physicals" />
						<Tag label={hasOmStandard ? 'No' : 'No (Not Available)'} state={omStandardState} onClick={() => {
          if (hasOmStandard) {
            handlePhysicalTagSelect('OM-STANDARD');
          }
        }} ariaLabel="No, I do not require any of these types of physicals" />
					</Stack>
				</Box>}
			{!accountIdError && !showLocationError && <Stack className={classes['button-container']}>
					<Button className={classes['button']} disabled={false} onClick={handleScheduleClick} aria={{
        label: `Find available visits for ${selectedTag || 'selected reason'}`
      }}>
						Schedule Your Visit
					</Button>
					{showError && visitReasonValidation.errorMessage && <Box style={{
        color: '#d32f2f',
        fontSize: '14px',
        marginTop: '8px',
        textAlign: 'center'
      }}>
							{visitReasonValidation.errorMessage}
						</Box>}
				</Stack>}
			{showLocationError && <LocationErrorMessage />}

			{/* Emergency Alert Dialog */}
			<EmergencyAlertDialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)} data-sentry-element="EmergencyAlertDialog" data-sentry-source-file="HeroLocation.tsx" />
			<InjuryNotificationDialog open={injuryDialogOpen} onClose={() => setInjuryDialogOpen(false)} urgentCareCentersLink={urgentCareCentersLink} data-sentry-element="InjuryNotificationDialog" data-sentry-source-file="HeroLocation.tsx" />
		</Box>;
}